import { z } from "zod"

export const MTTRolesEnum = z.enum([
  "admin",
  // "backoffice",
  "teacher",
  "teacherMentor",
  "finance",
  "contentAdvisor",
  "operationalManagement",
  "relationshipManager",
  "projectCoordinator",
  "executiveEmployee",
  "facility",
  "tester",
])

export type MTTRoles = z.infer<typeof MTTRolesEnum>

export const MTT_ROLE_TRANSLATIONS: {
  [role in MTTRoles]: string
} = {
  admin: "Admin",
  // backoffice: "Backoffice",
  teacher: "Docent",
  teacherMentor: "Docentenbegeleider",
  finance: "Finance",
  contentAdvisor: "Inhoudelijk adviseur",
  operationalManagement: "Operationeel management",
  relationshipManager: "Relatiemanager",
  projectCoordinator: "Projectcoördinator",
  executiveEmployee: "Uitvoerend medewerker",
  facility: "Facilitair",
  tester: "Tester",
}

// TODO: Implement permissions for teachingMaterials and exams when these are defined together with TopTaal.
// For now, they are included in the admin role.
export type MTTEntities =
  | "BSN"
  | "NAW"
  | "attendance"
  | "laptops"
  | "contacts"
  | "correspondence"
  | "studentCorrespondence"
  | "groupCorrespondence"
  | "projectCorrespondence"
  | "dashboard"
  | "duo"
  | "examResults"
  | "instructors"
  | "intake"
  | "invoices"
  | "curriculum" // learning paths and modules
  | "locations"
  | "organizations"
  | "planning"
  | "projects"
  | "students"
  | "testResults"
  | "employees"
  | "teacherQualifications"
  | "groups"
  | "teachingMaterials"
  | "exams"
  | "holidays"
  | "calendar"
  | "testSupport"

export type MTTPermissions = "create" | "read" | "update" | "delete"

export const MTT_ROLE_PERMISSIONS: {
  [role in MTTRoles]: Partial<Record<MTTEntities, MTTPermissions[]>>
} = {
  operationalManagement: {
    students: ["read"],
    intake: ["read"],
    planning: ["read"],
    invoices: ["create", "read", "update"],
    employees: ["read"],
    instructors: ["create", "read"],
    organizations: ["read"],
    contacts: ["read"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    laptops: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    dashboard: ["read", "update"],
    BSN: ["read"],
    groups: ["read"],
    holidays: ["read"],
  },
  projectCoordinator: {
    students: ["create", "delete", "read", "update"],
    intake: ["create", "delete", "read", "update"],
    planning: ["create", "delete", "read", "update"],
    invoices: ["create", "read", "update"],
    employees: ["read"],
    instructors: ["create", "delete", "read", "update"],
    organizations: ["read"],
    contacts: ["create", "read", "update"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    laptops: ["create", "delete", "read", "update"],
    correspondence: ["read", "create"],
    projectCorrespondence: ["read", "create"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read", "create"],
    examResults: ["read"],
    dashboard: ["read", "update"],
    BSN: ["create", "read", "update"],
    groups: ["create", "delete", "read", "update"],
    holidays: ["read"],
    calendar: ["read"],
  },
  executiveEmployee: {
    students: ["create", "delete", "read", "update"],
    intake: ["create", "delete", "read", "update"],
    planning: ["create", "delete", "read", "update"],
    invoices: ["create", "read", "update"],
    employees: ["read"],
    instructors: ["create", "delete", "read", "update"],
    organizations: ["read"],
    contacts: ["create", "read", "update"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    laptops: ["create", "read", "update"],
    correspondence: ["read", "create"],
    groupCorrespondence: ["read", "create"],
    studentCorrespondence: ["read", "create"],
    examResults: ["read"],
    dashboard: ["read"],
    BSN: ["create", "read", "update"],
    groups: ["create", "delete", "read", "update"],
    holidays: ["read"],
    calendar: ["read"],
  },
  teacherMentor: {
    students: ["read"],
    intake: ["read", "update"],
    planning: ["read"],
    employees: ["read"],
    teacherQualifications: ["read", "update"],
    instructors: ["create", "delete", "read", "update"],
    curriculum: ["read"],
    locations: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    dashboard: ["read"],
    groups: ["read"],
    holidays: ["read"],
  },
  teacher: {
    students: ["read"],
    intake: ["read", "update"],
    instructors: ["read"],
    locations: ["read"],
    correspondence: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    dashboard: ["read"],
    groups: ["read"],
    holidays: ["read"],
    calendar: ["read"],
  },
  contentAdvisor: {
    employees: ["read"],
    instructors: ["create", "delete", "read", "update"],
    organizations: ["read"],
    contacts: ["read"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    holidays: ["read"],
  },
  // backoffice: {
  //   students: ["read"],
  //   planning: ["read"],
  //   employees: ["read"],
  //   instructors: ["read"],
  //   organizations: ["read"],
  //   contacts: ["read"],
  //   projects: ["read"],
  //   locations: ["read"],
  //   studentCorrespondence: ["read"],
  //   groupCorrespondence: ["read"],
  //   projectCorrespondence: ["read"],
  //   examResults: ["read"],
  //   dashboard: ["read"],
  //   groups: ["read"],
  //   holidays: ["read"],
  // },
  relationshipManager: {
    students: ["read"],
    intake: ["read"],
    planning: ["read"],
    invoices: ["read"],
    employees: ["read"],
    instructors: ["read"],
    organizations: ["read"],
    contacts: ["read"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    dashboard: ["read"],
    groups: ["read"],
    holidays: ["read"],
  },
  finance: {
    students: ["read"],
    intake: ["read"],
    planning: ["read"],
    invoices: ["create", "delete", "read", "update"],
    employees: ["read"],
    instructors: ["read"],
    organizations: ["read"],
    contacts: ["read"],
    projects: ["read"],
    curriculum: ["read"],
    locations: ["read"],
    laptops: ["read"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["read"],
    dashboard: ["read"],
    BSN: ["create", "delete", "read", "update"],
    groups: ["read"],
    holidays: ["read"],
    duo: ["create", "read"],
  },
  admin: {
    calendar: ["create", "delete", "read", "update"],
    students: ["create", "delete", "read", "update"],
    intake: ["create", "delete", "read", "update"],
    planning: ["create", "delete", "read", "update"],
    invoices: ["create", "delete", "read", "update"],
    employees: ["create", "delete", "read", "update"],
    teacherQualifications: ["read", "update"],
    instructors: ["create", "delete", "read", "update"],
    organizations: ["create", "delete", "read", "update"],
    contacts: ["create", "delete", "read", "update"],
    projects: ["create", "delete", "read", "update"],
    curriculum: ["create", "delete", "read", "update"],
    locations: ["create", "delete", "read", "update"],
    laptops: ["create", "delete", "read", "update"],
    studentCorrespondence: ["read"],
    groupCorrespondence: ["read"],
    projectCorrespondence: ["read"],
    examResults: ["create", "delete", "read", "update"],
    dashboard: ["create", "delete", "read", "update"],
    BSN: ["create", "delete", "read", "update"],
    groups: ["create", "delete", "read", "update"],
    teachingMaterials: ["create", "delete", "read", "update"],
    exams: ["create", "delete", "read", "update"],
    holidays: ["create", "delete", "read", "update"],
    duo: ["create", "read"],
  },
  facility: {
    locations: ["read"],
  },
  tester: {
    testSupport: ["create", "read"],
  },
} as const

// MTT Functions - job titles/positions mapped to roles
export const MTTFunctionsEnum = z.enum([
  "programManager",
  "qualityInnovationManager",
  "qualityEmployee",
  "functionalApplicationManager",
  "ictEmployee",
  "teamLeaderIct",
  "ictManager",
  "supportEmployeeApplications",
  "languageTrainer",
  "languageTrainerTrainee",
  "teacherCoordinator",
  "teacherAdministrationEmployee",
  "languageTrainerPlus",
  "projectController",
  "seniorFinancialEmployee",
  "financialController",
  "financeManager",
  "seniorInvoiceEmployee",
  "coordinatorContentTeam",
  "contentAdvisor",
  "operationalManager",
  "regionalManager",
  "generalDirector",
  "projectCoordinator",
  "trainingAdviceManager",
  "relationshipManager",
  "accountManager",
  "projectPlanner",
  "administrativeEmployee",
  "tester",
])

export type MTTFunctions = z.infer<typeof MTTFunctionsEnum>

export type LegacyMTTFunction = "unknown"

export const MTT_FUNCTION_TRANSLATIONS: {
  [func in MTTFunctions | LegacyMTTFunction]: string
} = {
  unknown: "Onbekend",
  programManager: "Procesmanager",
  qualityInnovationManager: "Manager Kwaliteit & Innovatie",
  qualityEmployee: "Kwaliteitsmedewerker",
  functionalApplicationManager: "Functioneel Applicatiebeheerder MTT & AFAS",
  ictEmployee: "ICT medewerker",
  teamLeaderIct: "Teamleider ICT",
  ictManager: "Manager ICT",
  supportEmployeeApplications: "Supportmedewerker Applicaties",
  languageTrainer: "Taaltrainer",
  languageTrainerTrainee: "Taaltrainer trainee",
  teacherCoordinator: "Docentencoördinator",
  teacherAdministrationEmployee: "Medewerker docentenadministratie",
  languageTrainerPlus: "Taaltrainer plus",
  projectController: "Projectcontroller",
  seniorFinancialEmployee: "Senior Financieel Medewerker",
  financialController: "Financial Controller",
  financeManager: "Manager Finance",
  seniorInvoiceEmployee: "Senior Medewerker Facturatie",
  coordinatorContentTeam: "Teamcoördinator Inhoud",
  contentAdvisor: "Inhoudelijk adviseur",
  operationalManager: "Operationeel manager",
  regionalManager: "Regiomanager",
  generalDirector: "Algemeen Directeur",
  projectCoordinator: "Projectcoördinator",
  trainingAdviceManager: "Manager Relatie- en Contractmanagement",
  relationshipManager: "Relatiemanager",
  accountManager: "Accountmanager",
  projectPlanner: "Projectplanner",
  administrativeEmployee: "Administratief Medewerker",
  tester: "Tester",
}

export const MTT_ROLE_FUNCTIONS: {
  [role in MTTRoles]: MTTFunctions[]
} = {
  admin: [
    "programManager",
    "qualityInnovationManager",
    "qualityEmployee",
    "functionalApplicationManager",
    "ictEmployee",
    "teamLeaderIct",
    "ictManager",
    "supportEmployeeApplications",
  ],
  // backoffice: [],
  teacher: ["languageTrainer", "languageTrainerTrainee"],
  teacherMentor: [
    "teacherCoordinator",
    "teacherAdministrationEmployee",
    "languageTrainerPlus",
  ],
  finance: [
    "projectController",
    "seniorFinancialEmployee",
    "financialController",
    "financeManager",
    "seniorInvoiceEmployee",
  ],
  contentAdvisor: ["coordinatorContentTeam", "contentAdvisor"],
  operationalManagement: [
    "operationalManager",
    "regionalManager",
    "generalDirector",
  ],
  projectCoordinator: ["projectCoordinator"],
  relationshipManager: [
    "trainingAdviceManager",
    "relationshipManager",
    "accountManager",
  ],
  executiveEmployee: ["projectPlanner", "administrativeEmployee"],
  facility: [],
  tester: ["tester"],
}

export function getAvailableFunctions(roles: MTTRoles[]): MTTFunctions[] {
  if (!roles || roles.length === 0) {
    return []
  }

  return Array.from(
    new Set(roles.flatMap((role) => MTT_ROLE_FUNCTIONS[role] || [])),
  )
}

export const MTT_ROLE_OPTIONS = Object.entries(MTT_ROLE_TRANSLATIONS).map(
  ([value, label]) => ({
    value: value as MTTRoles,
    label,
  }),
)

export function getFunctionOptions(availableFunctions: MTTFunctions[]) {
  return availableFunctions.map((func) => ({
    value: func,
    label: MTT_FUNCTION_TRANSLATIONS[func],
  }))
}

export const ROLE_ERROR_EXPLANATIONS: Partial<Record<MTTRoles, string>> = {
  projectCoordinator: "gekoppeld is aan een contract (als projectcoördinator)",
  executiveEmployee:
    "gekoppeld is aan een contract (als planner of administratief medewerker)",
  teacher: "gekoppeld is aan een rooster van een groep en/of les",
  operationalManagement: "gekoppeld is aan een contract (als regiomanager)",
}
